import type { StyleSpecification } from 'maplibre-gl';

const PROXY_PATH_PREFIX = '/api/arcgis-proxy/';

function getCsrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function getBasemapStyleUrl(style: string, worldview?: string): string {
  const params = new URLSearchParams();
  if (worldview) params.set('worldview', worldview);
  return `${PROXY_PATH_PREFIX}basemap-style/${encodeURIComponent(style)}${
    params.toString() ? '?' + params.toString() : ''
  }`;
}

/**
 * Wrap an upstream raster XYZ tile template so it is fetched through the
 * arcgis-proxy `tile` endpoint (same-origin, host-allowlisted, server-cached).
 *
 * Mirrors the server-side style-rewriter: the upstream URL is percent-encoded
 * but the MapLibre `{z}`/`{y}`/`{x}` template tokens are restored to literals so
 * MapLibre can substitute them per tile. `transformProxyRequest` later makes the
 * `/api/arcgis-proxy/` path absolute and attaches credentials.
 */
function getProxiedTileUrl(tileTemplate: string): string {
  const encoded = encodeURIComponent(tileTemplate).replace(/%7B/gi, '{').replace(/%7D/gi, '}');
  return `${PROXY_PATH_PREFIX}tile/${encoded}`;
}

function transformProxyRequest(url?: string) {
  if (url?.startsWith(PROXY_PATH_PREFIX)) {
    // Absolute URL required -- MapLibre loads tiles in web workers
    // where relative URLs can't be resolved
    return {
      url: `${window.location.origin}${url}`,
      credentials: 'include' as const,
    };
  }
  return { url: url ?? '' };
}

async function fetchBasemapStyle(style: string, worldview?: string): Promise<StyleSpecification> {
  // 'no-cache' revalidates against the server ETag instead of trusting the
  // 24h max-age, so a redeployed style rewrite takes effect immediately
  const response = await fetch(getBasemapStyleUrl(style, worldview), {
    credentials: 'include',
    cache: 'no-cache',
  });
  if (!response.ok) {
    throw new Error(`Basemap style request failed: ${response.status}`);
  }
  const styleJson = (await response.json()) as StyleSpecification;
  // MapLibre validates the sprite URL with new URL() before transformRequest
  // runs, so the server-rewritten relative proxy path must be absolutized here.
  // Glyph and tile URLs are only resolved at request time, where
  // transformProxyRequest handles them. Sprites the server did not rewrite to
  // the proxy are dropped rather than fetched -- sprite loads must not bypass
  // the proxy's host allowlist.
  if (typeof styleJson.sprite === 'string') {
    if (styleJson.sprite.startsWith(PROXY_PATH_PREFIX)) {
      styleJson.sprite = `${window.location.origin}${styleJson.sprite}`;
    } else {
      console.warn(`Dropping non-proxied sprite URL: ${styleJson.sprite}`);
      delete styleJson.sprite;
    }
  } else if (Array.isArray(styleJson.sprite)) {
    const [proxied, dropped] = styleJson.sprite.reduce<[typeof styleJson.sprite, string[]]>(
      ([keep, drop], entry) => {
        if (entry.url.startsWith(PROXY_PATH_PREFIX)) {
          keep.push({ ...entry, url: `${window.location.origin}${entry.url}` });
        } else {
          drop.push(entry.url);
        }
        return [keep, drop];
      },
      [[], []]
    );
    if (dropped.length > 0) {
      console.warn(`Dropping non-proxied sprite URLs: ${dropped.join(', ')}`);
    }
    if (proxied.length > 0) {
      styleJson.sprite = proxied;
    } else {
      delete styleJson.sprite;
    }
  }
  return styleJson;
}

export {
  getCsrfToken,
  getBasemapStyleUrl,
  getProxiedTileUrl,
  transformProxyRequest,
  fetchBasemapStyle,
};
